import { useEffect, useState } from "react";
import { Box, Avatar, Typography } from "@mui/material";
import { DataGridTable } from "../DataGridTable/DataGridTable";
import { ColumnText } from "../DataGridTable/ColumnText";
import { ColumnButton } from "../DataGridTable/ColumnButton";
import { HeaderGridTable } from "../DataGridTable/HeaderGridTable";
import SchoolIcon from "@mui/icons-material/School";
import AlertDialog from "../AlertDialog";
import ModalInfo from "./Modal/ModalInfo";
import {
  Courses,
  resetFilters,
  setDialog,
  setFilters,
  setFiltersDialog,
} from "../../app/slices/Course/coursesSlice";
import { useDebounce } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";

export const Table = () => {
  const dispatch = useDispatch();
  const {
    courses: { data, loading, filters, total },
    manageCourse,
  } = useSelector((state) => state.courses);
  const [search, setSearch] = useState(null);
  const [debouncedSearch] = useDebounce(search, 600);
  const [deleteRowId, setDeleteRowId] = useState(null);

  const handleEdit = (data) => {
    dispatch(
      setDialog({
        id: data.id,
        name: data.name,
        description: data.description,
        duration: data.duration,
        seats_count: data.seats_count,
        price: data.price,
        company_id: data.company,
        is_active: data.is_active,
        apply_link: data.apply_link,
      })
    );
  };

  const columns = [
    {
      field: "name",
      headerName: "Course Name",
      headerClassName: "employer-grid--header",
      flex: 1.2,
      renderCell: (params) => {
        return (
          <Box>
            <ColumnText
              text={params.row?.name}
              variant="subtitle1"
              fontWeight="bold"
            />
          </Box>
        );
      },
    },
    {
      field: "company_name",
      headerName: "Company Name",
      headerClassName: "employer-grid--header",
      flex: 1.4,
      renderCell: (params) => {
        return (
          <Box display={"flex"} alignItems={"center"} gap={2}>
            <Avatar src={params.row?.company?.profile_pic} sizes={"small"} />
            <Box display={"flex"} flexDirection={"column"}>
              <ColumnText
                text={params.row.company?.company_name}
                variant="subtitle2"
                fontWeight="bold"
              />
            </Box>
          </Box>
        );
      },
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 1,
      headerClassName: "employer-grid--header",
      renderCell: (params) => {
        return (
          <ColumnText
            text={params.row?.duration}
            variant="subtitle2"
            fontWeight="bold"
          />
        );
      },
    },
    {
      field: "seats_count",
      headerName: "Seats",
      flex: 0.8,
      headerClassName: "employer-grid--header",
      renderCell: (params) => {
        return (
          <ColumnText
            text={params.row?.seats_count}
            variant="subtitle2"
            fontWeight="bold"
          />
        );
      },
    },
    {
      field: "price",
      headerName: "Price",
      flex: 1,
      headerClassName: "employer-grid--header",
      renderCell: (params) => {
        return (
          <ColumnText
            text={params.row?.formatted_price}
            variant="subtitle2"
            fontWeight="bold"
          />
        );
      },
    },
    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
      headerClassName: "employer-grid--header",
      renderCell: (params) => {
        return (
          <ColumnText
            text={params.row?.created_by}
            variant="subtitle2"
            fontWeight="bold"
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      headerClassName: "employer-grid--header",
      renderCell: (params) => {
        return (
          <ColumnText
            text={params.row?.created_at?.split("T")[0]}
            variant="subtitle2"
            fontWeight="bold"
          />
        );
      },
    },
    {
      field: "operations",
      headerName: "Operations",
      sortable: false,
      flex: 1.2,
      headerClassName: "employer-grid--header",

      renderCell: (params) => {
        return (
          <Box display={"flex"} alignItems={"center"} gap={2}>
            <ColumnButton
              onClick={() => handleEdit(params.row)}
              type={"edit"}
            />

            <ColumnButton
              onClick={() => setDeleteRowId(params.row.id)}
              type={"delete"}
            />
            <AlertDialog
              open={deleteRowId === params.row.id}
              action={() => {
                dispatch(Courses.deleteCourse(params.row.id));
                setDeleteRowId(null);
              }}
              handleClose={() => setDeleteRowId(null)}
              title="Delete course"
              message={`Are you sure you want to delete (${params.row?.name})`}
            />
          </Box>
        );
      },
    },
  ];

  const fetchData = () => {
    dispatch(Courses.index());
  };

  useEffect(() => {
    if (debouncedSearch !== null) {
      dispatch(setFilters({ search: debouncedSearch }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [filters.skip, filters.take, filters.page, filters.search]);

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, []);

  return (
    <>
      <Box m={2}>
        <HeaderGridTable
          title="Courses"
          icon={<SchoolIcon size={25} color={"primary"} />}
          btn={"Add Course"}
          fetchData={fetchData}
          isFilter={false}
          handleSearch={(e) => {
            setSearch(e.target.value);
          }}
          handleAdd={() => dispatch(setDialog())}
        />
        <Box mt={2}>
          <DataGridTable
            rowCount={total}
            rows={data}
            loading={loading}
            page={filters.page}
            columns={columns}
            pageSize={filters.take}
            onPageChange={(newPage) => {
              dispatch(
                setFilters({
                  skip: newPage * filters.take,
                  page: newPage,
                })
              );
            }}
            onPageSizeChange={(newPageSize) => {
              dispatch(
                setFilters({
                  take: newPageSize,
                })
              );
              if (filters.skip === 0) {
                dispatch(Courses.index());
              } else {
                dispatch(
                  setFilters({
                    skip: 0,
                    page: 0,
                  })
                );
              }
            }}
          />
        </Box>
        {manageCourse.dialog && <ModalInfo />}
      </Box>
    </>
  );
};

export default Table; 